import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import _ from 'lodash';
import { types as coreTypes } from '@sitebuilder/core';
import { hooks as commonHooks } from '@sitebuilder/common';
import { formLocaleRegex } from '../../../utils';
import { StateShape as ReduxStateShape } from '../../../reducers';
import { actionCreators } from '../../../reducers/resources';
import { AddNewItemForm } from '..';
import { DataKeys } from '../AddNewItemForm';

interface AddNewLocaleFormProps {
    resources: coreTypes.Resource | null;
    resourcesType: string;
}

const idRegex = formLocaleRegex;

type FormData = coreTypes.SaveNewLocale;

const AddNewLocaleForm = (props: AddNewLocaleFormProps) => {
    const { resources, resourcesType } = props;
    const dispatch = useDispatch();
    const i18n = commonHooks.useLocalization();

    // initial form data
    const initialData = {
        id: '',
    };
    const [data, setData] = React.useState<FormData>(initialData);

    // error messages for invalid inputs
    const [dataError, setDataError] = React.useState<FormData>(initialData);

    // error message on locale save
    const [error, setError] = React.useState<string>('');

    // success message on locale save
    const [success, setSuccess] = React.useState<string>('');

    const { resourcesSaveLoading, resourcesSaveSuccess, resourcesSaveError } = useSelector(
        (state: ReduxStateShape) => ({
            ...state?.resources,
        })
    );

    useEffect(() => {
        if (resourcesSaveSuccess) {
            setSuccess(resourcesSaveSuccess);

            if (resourcesType === 'site') {
                dispatch(actionCreators.getResources());
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [resourcesSaveSuccess]);

    useEffect(() => {
        if (resourcesSaveError) {
            setError(i18n.t('new_locale_save_error'));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [resourcesSaveError]);

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>, field: DataKeys) => {
        const { value } = e?.target;

        if (error) {
            setError('');
        }

        if (success) {
            setSuccess('');
        }

        if (!value && dataError[field]) {
            setDataError({ ...dataError, [field]: '' });
        }

        if (field === 'id') {
            if (value.trim()) {
                if (!idRegex.test(value)) {
                    setDataError({ ...dataError, id: i18n.t('form_invalid_locale') });
                } else {
                    setDataError({ ...dataError, id: '' });
                }
            } else {
                setDataError({ ...dataError, id: i18n.t('form_missing_id') });
            }
        }

        setData({
            ...data,
            [field]: value,
        });
    };

    const validateFormData = () => {
        let valid = false;
        const { id } = data;
        const err: FormData = {
            id: '',
        };

        if (!id) {
            err.id = i18n.t('form_missing_id');
        }

        if (!_.values(err).every(_.isEmpty)) setDataError({ ...dataError, ...err });

        valid =
            _.every(err, (e) => e === '' || _.isNull(e)) &&
            _.every(dataError, (e) => e === '' || _.isNull(e));
        return valid;
    };

    const handleSubmit = async () => {
        const isFormValid = validateFormData();

        if (isFormValid) {
            const _locale = data.id;
            // check if resource with the same name exists
            const isNewLocale = resources ? !Object.keys(resources).includes(_locale) : true;

            if (!isNewLocale) {
                setError(i18n.t('locale_already_exists'));
                setSuccess('');
            } else {
                const newLocaleResources =
                    resources &&
                    Object.keys(resources['en-US']).reduce((acc: any, i: string) => {
                        acc[i] = '';
                        return acc;
                    }, {});

                const _resources: any = { ...resources, [_locale]: { ...newLocaleResources } };
                await dispatch(actionCreators.saveResources(_resources, resourcesType, true));
                setData(initialData);
                setDataError(initialData);
            }
        }
    };

    return (
        <>
            <AddNewItemForm
                formTitle="Create New Locale"
                data={data}
                dataError={dataError}
                handleInputChange={handleInputChange}
                handleSubmit={handleSubmit}
                showDescription={false}
                showName={false}
                saveError={error}
                saveSuccess={success}
                showSaveLoading={resourcesSaveLoading || false}
                formInputsLabel={{ id: 'Locale Id' }}
                defaultActiveAccordionKey="0"
            />
        </>
    );
};

export default AddNewLocaleForm;
